'use client';

import { fetchProductList } from '@/api/product';
import type { ListProduct } from '@/api/types/public';
import { useSWRInfiniteScroll } from '@/hooks/useSWRInfiniteScroll';
import { serializeKey } from '@/utils/swr/keys';
import { useCallback, useMemo } from 'react';

const PAGE_SIZE = 20;

interface StyleProductRecommendResponse {
  code?: number;
  data?: {
    list?: ListProduct[];
    total?: number;
  };
}

interface StyleProductRecommendKeyParams {
  groupId: number;
  page: number;
  currency?: string;
}

export function buildStyleProductRecommendKey({
  groupId,
  page,
  currency,
}: StyleProductRecommendKeyParams): string {
  return serializeKey([
    'styleHotProduct',
    'groupRecommend',
    {
      groupId,
      page,
      pageSize: PAGE_SIZE,
      currency: currency || '',
    },
  ])!;
}

export function useStyleProductRecommend(groupId: number, currency?: string) {
  const firstPageKey = useMemo(
    () => (groupId > 0 ? buildStyleProductRecommendKey({ groupId, page: 1, currency }) : null),
    [currency, groupId],
  );
  const getKey = useCallback(
    (pageIndex: number, previousPageData: StyleProductRecommendResponse | null) => {
      if (groupId <= 0) return null;

      const previousList = previousPageData?.data?.list || [];
      if (previousPageData && previousList.length < PAGE_SIZE) return null;

      return buildStyleProductRecommendKey({ groupId, page: pageIndex + 1, currency });
    },
    [currency, groupId],
  );
  const fetcher = useCallback((key: string) => {
    const parsedKey = JSON.parse(key) as [string, string, StyleProductRecommendKeyParams];
    const params = parsedKey[2];

    return fetchProductList({
      group_id: params.groupId,
      page: params.page,
      limit: PAGE_SIZE,
      append_correlation: 1,
      ...(params.currency ? { currency: params.currency } : {}),
    }) as Promise<StyleProductRecommendResponse>;
  }, []);
  const result = useSWRInfiniteScroll<ListProduct, StyleProductRecommendResponse>({
    firstPageKey,
    getKey,
    extractList: (pageData) => pageData?.data?.list || [],
    checkIsReachingEnd: (pageData) => (pageData?.data?.list?.length || 0) < PAGE_SIZE,
    swrConfig: {
      fetcher,
    },
  });
  const { isReachingEnd, isValidating, setSize } = result;
  const loadMore = useCallback(() => {
    if (!isReachingEnd && !isValidating) {
      void setSize((current) => current + 1);
    }
  }, [isReachingEnd, isValidating, setSize]);

  return {
    ...result,
    loading: result.isLoading || result.isValidating,
    loadMore,
    pageSize: PAGE_SIZE,
    total: result.originalData?.[0]?.data?.total || 0,
  };
}
